import * as React from 'react'
import {useState, useRef, useEffect, useCallback} from 'react'
import {Box, PolymorphicComponentProps} from './Box'
import {Text} from './Text'
import {inputRecipe, iconContainer, InputVariants, inputLabelRecipe} from './input.css'
import classNames from 'classnames'

type StyleProps = object & InputVariants

type UVariant<T = StyleProps> = T extends InputVariants ? Omit<T, 'variant' | 'label'> : never

type InputOwnProps = UVariant & {
  icon?: React.ReactNode | null
  disabled?: boolean
  variant?: 'unstyled' | StyleProps['variant']
  hasError?: boolean
  label?: string
  as?: 'string'
  validatedSuccessfully: boolean
  labelDataTestSelector?: string
}

export type InputProps<E extends React.ElementType> = PolymorphicComponentProps<E, InputOwnProps>

const defaultElement = 'input'

export const Input: <E extends React.ElementType = typeof defaultElement>(
  props: InputProps<E>,
) => React.ReactElement | null = React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      variant,
      size,
      colorScheme,
      width = 'full',
      focusVisible = false,
      hasError,
      children,
      label,
      icon = null,
      value,
      validatedSuccessfully,
      name,
      className,
      onBlur,
      showAsterisk,
      id,
      labelDataTestSelector,
      ...restProps
    }: InputProps<E>,
    ref: typeof restProps.ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [isAutofilled, setIsAutofilled] = useState(false)

    const inputRef = useRef<HTMLInputElement | null>(null)

    const mergedRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
        }
      }, [ref],
    )

    useEffect(() => {
      const inputElement = inputRef.current
      if (!inputElement) return

      const handleAnimationStart = (e: AnimationEvent) => {
        if (e.animationName === 'onAutoFillStart') {
          setIsAutofilled(true)
        }
      }

      inputElement.addEventListener('animationstart', handleAnimationStart)
      return () => inputElement.removeEventListener('animationstart', handleAnimationStart)
    }, [])

    const isFilled = !!value || isFocused || isAutofilled

    const classNameInput = inputRecipe({
      variant,
      colorScheme,
      size,
      focusVisible,
      error: hasError,
      withLeftIcon: !!icon,
      validatedSuccessfully,
    })

    const classNameLabel = inputLabelRecipe({
      variant,
      colorScheme,
      size,
      isFilled,
      focusVisible,
      error: hasError,
      withLeftIcon: !!icon,
      validatedSuccessfully,
    })

    const {type: inputType, ...inputRestProps} = restProps

    return (
      <Box position="relative" width={width} display="flex">
        {icon && <Box className={iconContainer}>{icon}</Box>}
        <Box
          id={id}
          as="input"
          display="block"
          name={name}
          type={inputType as any}
          fontFamily="body"
          className={classNames(classNameInput, className)}
          onFocus={() => setIsFocused(true)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(e)
            setIsFocused(false)
          }}
          value={value}
          paddingRight={inputType === 'password' ? '44px' : undefined}
          ref={mergedRef}
          {...(inputRestProps as any)}
        />
        {label && (
          <Box as="label" htmlFor={id} aria-label={label} className={classNameLabel}>
            <Text
              variant="unstyled"
              as="span"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              fontWeight="inherit"
              fontSize="inherit"
              lineHeight="inherit"
              style={{height: 'inherit'}}
              paddingX={isFilled ? '4px' : '0px'}
              backgroundColor={isFilled ? 'white' : 'transparent'}
            >
              {label}
              {showAsterisk && (
                <Text
                  as="span"
                  display="inline-block"
                  marginLeft={isFilled ? '4px' : '0px'}
                  fontSize="inherit"
                  lineHeight="inherit"
                  fontWeight="inherit"
                  color="error"
                  marginBottom="2px"
                >
                  *
                </Text>
              )}
            </Text>
          </Box>
        )}
      </Box>
    )
  },
)
